import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { api } from "../lib/api";
import {
  Upload, Loader2, CheckCircle2, AlertTriangle, Pause, Play, X,
  ShieldCheck, Disc3, MessageCircle, RotateCw, FileArchive, HardDriveUpload,
  Mail, Copy,
} from "lucide-react";

/**
 * Public DICOM upload page — no login required, gated only by the token
 * in the URL. Backend endpoints:
 *   GET   /dicom-upload/session/:token
 *   POST  /dicom-upload/:token/init             { filename, total_size, content_type }
 *   POST  /dicom-upload/:token/sign-part/:id/:n
 *   POST  /dicom-upload/:token/complete/:id     { parts }
 *   POST  /dicom-upload/:token/abort/:id
 *
 * We upload each chunk DIRECTLY to R2/S3 via a short-lived presigned URL
 * so the browser bytes never pass through our app pod.
 */

// ---------- Small helpers ----------
const fmtBytes = (n) => {
  if (!n || n < 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0; let x = n;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};
const fmtETA = (secs) => {
  if (!isFinite(secs) || secs < 0) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h ${Math.round((secs % 3600) / 60)}m`;
};

// Retry primitive — exponential back-off with jitter, aborts if user pauses.
async function retryable(fn, { retries = 4, baseMs = 500, isAborted = () => false } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (isAborted()) throw new Error("aborted");
    try { return await fn(attempt); }
    catch (e) {
      lastErr = e;
      if (attempt === retries) break;
      const jitter = 100 + Math.floor(Math.random() * 400);
      await new Promise((r) => setTimeout(r, baseMs * 2 ** attempt + jitter));
    }
  }
  throw lastErr;
}

// PUT a single chunk to a presigned R2/S3 URL, resolving with the ETag.
function putChunkWithProgress(url, blob, onProgress, abortSignal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    if (abortSignal) {
      abortSignal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === "function") {
        onProgress(e.loaded);
      }
    };
    xhr.onerror = () => reject(new Error("network"));
    xhr.onabort = () => reject(new Error("aborted"));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // R2 & S3 both surface ETag (lower- or mixed-case).
        const etag = xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag") || "";
        if (!etag) return reject(new Error("missing_etag"));
        resolve(etag.replaceAll('"', ""));
      } else {
        reject(new Error(`http_${xhr.status}`));
      }
    };
    xhr.send(blob);
  });
}

export default function PublicDicomUpload() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle");  // idle | uploading | paused | completed | failed
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [uploadStartedAt, setUploadStartedAt] = useState(null);
  const [rowId, setRowId] = useState(null);
  const [partsDone, setPartsDone] = useState({}); // { partNumber: etag }
  const [errorMsg, setErrorMsg] = useState("");
  const [showCdHelp, setShowCdHelp] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const abortRef = useRef(null);
  const pauseRef = useRef(false);

  // ---- Session bootstrap ----
  useEffect(() => {
    api.get(`/dicom-upload/session/${token}`)
      .then((r) => setSession(r.data))
      .catch((e) => {
        setSessionError(e?.response?.data?.detail || "This upload link is not valid.");
      });
  }, [token]);

  // ---- File selection / drag-drop ----
  const onFilePicked = useCallback((f) => {
    if (!f) return;
    if (!session) return;
    if (f.size > session.max_size_bytes) {
      toast.error(`File too large. Max ${session.max_size_mb} MB / this file is ${fmtBytes(f.size)}.`);
      return;
    }
    const name = f.name.toLowerCase();
    const ok = (session.allowed_extensions || []).some((ext) => name.endsWith(ext));
    if (!ok) {
      toast.error("Please upload a DICOM (.dcm) file or a ZIP of your study.");
      return;
    }
    setFile(f);
    setPhase("idle");
    setErrorMsg("");
    setUploadedBytes(0);
    setPartsDone({});
    setRowId(null);
  }, [session]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 1) {
      toast.info("Multiple files detected — please ZIP them and upload the ZIP so the study stays together.");
      return;
    }
    onFilePicked(files[0]);
  }, [onFilePicked]);

  // ---- Upload orchestration ----
  const startUpload = useCallback(async () => {
    if (!file || !session) return;
    setPhase("uploading");
    setErrorMsg("");
    setUploadStartedAt(Date.now());
    pauseRef.current = false;
    abortRef.current = new AbortController();

    let init;
    try {
      const r = await api.post(`/dicom-upload/${token}/init`, {
        filename: file.name,
        total_size: file.size,
        content_type: file.type || "application/octet-stream",
      });
      init = r.data;
      setRowId(init.upload_row_id);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Could not start upload.");
      setPhase("failed"); return;
    }

    const { upload_row_id, chunk_size, total_parts } = init;
    const partsMap = { ...partsDone };
    // Per-part progress bookkeeping
    const partProgress = {};
    const totalDone = () =>
      Object.values(partProgress).reduce((a, b) => a + b, 0);

    try {
      // Simple parallelism = 3 (S3 is happy up to 10; 3 keeps mobile in check).
      const CONCURRENCY = 3;
      const queue = [];
      for (let n = 1; n <= total_parts; n++) {
        if (partsMap[n]) continue;                      // already done (resume)
        queue.push(n);
      }
      let cursor = 0;
      const worker = async () => {
        while (cursor < queue.length) {
          if (pauseRef.current) return;
          const partNumber = queue[cursor++];
          const start = (partNumber - 1) * chunk_size;
          const end = Math.min(start + chunk_size, file.size);
          const blob = file.slice(start, end);
          const etag = await retryable(async () => {
            const s = await api.post(`/dicom-upload/${token}/sign-part/${upload_row_id}/${partNumber}`);
            partProgress[partNumber] = 0;
            const et = await putChunkWithProgress(
              s.data.url, blob,
              (loaded) => {
                partProgress[partNumber] = loaded;
                setUploadedBytes(totalDone());
              },
              abortRef.current.signal,
            );
            partProgress[partNumber] = end - start;
            return et;
          }, { retries: 4, baseMs: 800, isAborted: () => pauseRef.current });
          partsMap[partNumber] = etag;
          setPartsDone({ ...partsMap });
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));

      if (pauseRef.current) { setPhase("paused"); return; }

      const partsPayload = Object.entries(partsMap)
        .map(([pn, et]) => ({ PartNumber: Number(pn), ETag: et }))
        .sort((a, b) => a.PartNumber - b.PartNumber);
      await api.post(`/dicom-upload/${token}/complete/${upload_row_id}`, { parts: partsPayload });
      setUploadedBytes(file.size);
      setPhase("completed");
      toast.success("Upload complete!");
    } catch (e) {
      if (pauseRef.current || (e?.message || "").includes("aborted")) {
        setPhase("paused"); return;
      }
      setErrorMsg(e?.response?.data?.detail || e?.message || "Upload interrupted.");
      setPhase("failed");
    }
  }, [file, session, token, partsDone]);

  const pause = () => {
    pauseRef.current = true;
    try { abortRef.current?.abort(); } catch { /* noop */ }
    setPhase("paused");
    toast.info("Paused. Tap Resume to continue.");
  };
  const resume = () => { setPhase("uploading"); startUpload(); };
  const retry  = () => { setErrorMsg(""); startUpload(); };
  const cancel = async () => {
    pauseRef.current = true;
    try { abortRef.current?.abort(); } catch { /* noop */ }
    if (rowId) {
      try { await api.post(`/dicom-upload/${token}/abort/${rowId}`); } catch { /* noop */ }
    }
    setPhase("idle"); setFile(null); setPartsDone({}); setUploadedBytes(0); setRowId(null);
  };

  // ---- Derived UI values ----
  const pct = file ? Math.min(100, Math.round((uploadedBytes / file.size) * 100)) : 0;
  const eta = useMemo(() => {
    if (!file || !uploadStartedAt || uploadedBytes <= 0) return "—";
    const elapsed = (Date.now() - uploadStartedAt) / 1000;
    const rate = uploadedBytes / elapsed;
    return fmtETA((file.size - uploadedBytes) / rate);
  }, [file, uploadedBytes, uploadStartedAt]);

  // ---- Screens ----
  if (sessionError) {
    return (
      <ErrorShell title="Upload link not valid" body={sessionError} showSupport />
    );
  }
  if (!session) {
    return <LoadingShell />;
  }
  if (phase === "completed") {
    return <SuccessShell caseRef={session.case_ref} filename={file?.name} size={file?.size}/>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header caseRef={session.case_ref} expiresAt={session.expires_at}/>
      <main className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
          Upload your CT / MRI DICOM study
        </h1>
        <p className="mt-2 text-slate-600 text-sm sm:text-base">
          Please upload the <b>complete DICOM study</b> your hospital or diagnostic centre gave you.
          Case ID: <span className="font-mono font-semibold text-slate-900">{session.case_ref}</span>
        </p>

        {/* File picker / drag-drop */}
        {!file && (
          <Card
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            className={`mt-6 border-2 border-dashed p-8 sm:p-12 text-center transition-colors ${
              dragActive ? "border-teal-500 bg-teal-50" : "border-slate-300 bg-white"
            }`}
            data-testid="dicom-drop-zone"
          >
            <HardDriveUpload className="w-12 h-12 text-teal-600 mx-auto"/>
            <div className="mt-3 font-heading font-semibold text-slate-900 text-lg">
              Drag your DICOM files or ZIP here
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Accepted: .dcm / .dicom / .zip · Max {session.max_size_mb.toLocaleString()} MB per upload
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
              <label className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold cursor-pointer" data-testid="dicom-pick-file">
                <Upload className="w-4 h-4"/> Select DICOM files
                <input type="file" accept=".dcm,.dicom,application/dicom" className="hidden" onChange={(e) => onFilePicked(e.target.files[0])}/>
              </label>
              <label className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold cursor-pointer" data-testid="dicom-pick-zip">
                <FileArchive className="w-4 h-4"/> Upload ZIP file
                <input type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => onFilePicked(e.target.files[0])}/>
              </label>
            </div>
          </Card>
        )}

        {/* Upload panel */}
        {file && phase !== "idle" && (
          <Card className="mt-6 p-6 border-slate-200" data-testid="dicom-upload-panel">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-50 grid place-items-center shrink-0">
                {phase === "failed" ? <AlertTriangle className="w-5 h-5 text-rose-600"/>
                  : phase === "paused" ? <Pause className="w-5 h-5 text-amber-600"/>
                  : <Loader2 className="w-5 h-5 text-teal-600 animate-spin"/>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-semibold text-slate-900 truncate">{file.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {fmtBytes(uploadedBytes)} / {fmtBytes(file.size)} · ETA {eta}
                </div>
              </div>
              <div className="text-xl font-heading font-bold text-slate-900 tabular-nums" data-testid="dicom-upload-pct">{pct}%</div>
            </div>
            <Progress value={pct} className="mt-4 h-2.5"/>

            {phase === "uploading" && (
              <div className="mt-4 flex gap-2 justify-end">
                <Button variant="outline" onClick={pause} className="h-9" data-testid="dicom-pause">
                  <Pause className="w-4 h-4 mr-1"/> Pause
                </Button>
                <Button variant="ghost" onClick={cancel} className="h-9 text-slate-500" data-testid="dicom-cancel">
                  <X className="w-4 h-4 mr-1"/> Cancel
                </Button>
              </div>
            )}
            {phase === "paused" && (
              <>
                <p className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                  Paused — your progress is preserved. Tap Resume to continue where you left off.
                </p>
                <div className="mt-3 flex gap-2 justify-end">
                  <Button variant="outline" onClick={cancel} className="h-9 text-slate-500" data-testid="dicom-cancel-paused">Cancel</Button>
                  <Button onClick={resume} className="h-9 bg-teal-600 hover:bg-teal-700" data-testid="dicom-resume">
                    <Play className="w-4 h-4 mr-1"/> Resume
                  </Button>
                </div>
              </>
            )}
            {phase === "failed" && (
              <>
                <p className="mt-4 text-sm text-slate-700 bg-rose-50 border border-rose-200 rounded p-3">
                  <b>Upload interrupted.</b> {errorMsg ? `— ${errorMsg}` : ""} Your uploaded data has been preserved.
                </p>
                <div className="mt-3 flex flex-wrap gap-2 justify-end">
                  <a
                    href={session.support_whatsapp}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-teal-700 hover:underline"
                    data-testid="dicom-support-wa"
                  >
                    <MessageCircle className="w-3.5 h-3.5"/> Contact support on WhatsApp
                  </a>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={cancel} className="h-9 text-slate-500">Cancel</Button>
                    <Button onClick={retry} className="h-9 bg-teal-600 hover:bg-teal-700" data-testid="dicom-retry">
                      <RotateCw className="w-4 h-4 mr-1"/> Retry
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        )}

        {/* Confirm / start */}
        {file && phase === "idle" && (
          <Card className="mt-6 p-5 border-slate-200">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-heading font-semibold text-slate-900 truncate">{file.name}</div>
                <div className="text-xs text-slate-500">{fmtBytes(file.size)}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setFile(null)} className="text-slate-500 h-9">Change</Button>
                <Button onClick={startUpload} className="bg-teal-600 hover:bg-teal-700 h-9" data-testid="dicom-start-upload">
                  <Upload className="w-4 h-4 mr-1"/> Start upload
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Already-uploaded studies */}
        {session.already_uploaded?.length > 0 && (
          <div className="mt-8">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Uploaded to this case
            </div>
            <div className="space-y-2">
              {session.already_uploaded.map((u) => (
                <div key={u.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid={`already-uploaded-${u.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{u.filename}</div>
                    <div className="text-xs text-slate-500">
                      {fmtBytes(u.size)} · {new Date(u.completed_at).toLocaleString()}
                    </div>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0"/>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CD / DVD help */}
        <div className="mt-8 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <button
            onClick={() => setShowCdHelp((v) => !v)}
            className="w-full flex items-center gap-2 p-4 text-left hover:bg-slate-50 transition"
            data-testid="dicom-cd-help-toggle"
          >
            <Disc3 className="w-5 h-5 text-slate-500"/>
            <span className="font-heading font-semibold text-slate-900 flex-1">
              I only have a DICOM CD / DVD
            </span>
            <span className="text-slate-400 text-xs">{showCdHelp ? "hide" : "show"}</span>
          </button>
          {showCdHelp && (
            <div className="px-5 pb-5 pt-1 text-sm text-slate-700 space-y-2" data-testid="dicom-cd-help-body">
              <ol className="list-decimal pl-5 space-y-1.5">
                <li>Insert the CD/DVD into a computer that has a disc drive (or a USB DVD drive).</li>
                <li>Open the CD/DVD and locate the folder labelled <b>DICOM</b> (sometimes <b>DICOMDIR</b>).</li>
                <li>Copy the <b>entire folder</b> to your computer's desktop.</li>
                <li>Right-click the folder → <b>"Send to → Compressed (zipped) folder"</b> (Windows) or <b>"Compress"</b> (Mac).</li>
                <li>Open this upload link on the same computer and drop the ZIP above.</li>
              </ol>
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                <b>Please don't send screenshots or JPGs of your scans.</b> The original DICOM files give the doctor 3-D data that screenshots can't preserve.
              </p>
            </div>
          )}
        </div>

        {/* Save this link — resume from a different device / later */}
        <SaveLinkCard caseRef={session.case_ref} expiresAt={session.expires_at} />

        {/* Trust footer */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/> Encrypted upload</span>
          <span>·</span>
          <span>Private storage — only your care team can view</span>
          <span>·</span>
          <a href={session.support_whatsapp} target="_blank" rel="noreferrer" className="hover:text-teal-700">Need help?</a>
        </div>
      </main>
    </div>
  );
}

// ---------- Sub-components ----------
function SaveLinkCard({ caseRef, expiresAt }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const message =
    `NeuroOpinion secure upload link for case ${caseRef} — please upload your CT/MRI DICOM study here (valid ~72 hours):\n${url}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied — paste it anywhere to resume later"); }
    catch { toast.error("Couldn't copy. Long-press the URL bar to copy the link manually."); }
  };
  const shareWa = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener");
  };
  const shareEmail = () => {
    window.location.href =
      `mailto:?subject=${encodeURIComponent(`NeuroOpinion upload — case ${caseRef}`)}&body=${encodeURIComponent(message)}`;
  };
  return (
    <Card className="mt-6 p-5 border-slate-200 bg-slate-50/60" data-testid="save-link-card">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
        Can't finish right now?
      </div>
      <h3 className="mt-1 font-heading text-lg font-semibold text-slate-900">
        Save this link — resume from another device
      </h3>
      <p className="mt-1 text-xs text-slate-600 leading-relaxed">
        Send the link to yourself so you can pick up on a computer with a better connection or a disc drive.
        The same link stays valid for the full 72 hours — you don't need a new one.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={copyLink} variant="outline" className="h-9 text-xs" data-testid="save-link-copy">
          <Copy className="w-3.5 h-3.5 mr-1"/> Copy link
        </Button>
        <Button onClick={shareWa} variant="outline" className="h-9 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="save-link-whatsapp">
          <MessageCircle className="w-3.5 h-3.5 mr-1"/> Send on WhatsApp
        </Button>
        <Button onClick={shareEmail} variant="outline" className="h-9 text-xs" data-testid="save-link-email">
          <Mail className="w-3.5 h-3.5 mr-1"/> Email to myself
        </Button>
      </div>
    </Card>
  );
}


function Header({ caseRef, expiresAt }) {
  const exp = expiresAt ? new Date(expiresAt).toLocaleString() : "—";
  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
        <div className="font-heading font-bold text-lg text-slate-900">NeuroOpinion</div>
        <div className="text-right text-xs">
          <div className="font-mono font-semibold text-slate-900">{caseRef}</div>
          <div className="text-slate-500">Link valid until {exp}</div>
        </div>
      </div>
    </header>
  );
}

function LoadingShell() {
  return (
    <div className="min-h-screen grid place-items-center text-slate-500">
      <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading upload…</div>
    </div>
  );
}

function ErrorShell({ title, body, showSupport }) {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-4">
      <Card className="max-w-md p-6 text-center" data-testid="dicom-error-shell">
        <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto"/>
        <h2 className="mt-3 font-heading font-bold text-xl text-slate-900">{title}</h2>
        <p className="mt-2 text-sm text-slate-600">{body}</p>
        {showSupport && (
          <div className="mt-4 space-y-1 text-xs text-slate-500">
            <div><b>Support:</b> support@neuroopinion.in</div>
            <a href="https://wa.me/919441724072" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal-700 hover:underline">
              <MessageCircle className="w-3.5 h-3.5"/> Message us on WhatsApp
            </a>
          </div>
        )}
      </Card>
    </div>
  );
}

function SuccessShell({ caseRef, filename, size }) {
  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-b from-emerald-50 to-white p-4">
      <Card className="max-w-md p-8 text-center" data-testid="dicom-success-shell">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto"/>
        <h2 className="mt-4 font-heading font-bold text-2xl text-slate-900">DICOM upload successful</h2>
        <p className="mt-2 text-sm text-slate-600">
          Your CT / MRI study has been <b>securely uploaded</b>. Our medical team can now access your scan for review.
        </p>
        <div className="mt-5 space-y-1 text-sm">
          <div>Case: <span className="font-mono font-semibold text-slate-900">{caseRef}</span></div>
          {filename && <div className="text-slate-500 text-xs truncate">File: {filename} ({fmtBytes(size)})</div>}
        </div>
        <p className="mt-6 text-xs text-slate-500">You may close this page.</p>
      </Card>
    </div>
  );
}
